import { HostListener, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, of, throwError, tap, interval, Subscription } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ConfigurationService } from '../utils/configuration.service';
import { SessionService } from '../session/session.service';
import { SnackBarService } from '@app/shared/components/snackbar/snackbar.service';
import { SnackMessage } from '@app/shared/interfaces/snackbar.interface';
import { SnackBarsTypes } from '@app/shared/enums/snackbars';

enum errorSeverities {
  ERROR = 'Error',
  WARNING = 'Warning',
}

@Injectable({
  providedIn: 'root',
})
export class DataService {
  apiUrl = '';

  private resetCache$: Subscription;

  constructor(
    private http: HttpClient,
    private configurationService: ConfigurationService,
    private sessionService: SessionService,
    private snackBarService: SnackBarService
  ) {
    this.apiUrl = `${this.configurationService.getApiUrl()}/api/internal/`;
    this.resetCache();
  }

  // Stop interval once the user close application tab or browser
  @HostListener('window:beforeunload', [])
  public beforeUnload(): void {
    this.resetCache$.unsubscribe();
  }

  get<T>(url: string, useCache?: boolean): Observable<T | any> {
    const requestUrl = `${this.apiUrl}${url}`;
    return this.http.get<T>(requestUrl).pipe(
      tap((response) => {
        if (useCache) {
          const split = url.split('/');

          const lastString = split[1].indexOf('?') > 0 ? split[1].substring(0, split[1].indexOf('?')) : split[1];
          const urlSplit = `${split[0]}_${lastString}`;
          this.sessionService.setItem(urlSplit.toUpperCase(), response);
        }
      }),
      map((res: T) => res),
      catchError(this.handleHttpError)
    );
  }

  getWithParams<T>(url: string, paramsObj: { [key: string]: any }, useCache?: boolean): Observable<T | any> {
    let params = new HttpParams();

    if (paramsObj) {
      Object.keys(paramsObj).forEach(key => {
        if (paramsObj[key] !== undefined && paramsObj[key] !== null) {
          params = params.set(key, paramsObj[key]);
        }
      });
    }

    const requestUrl = `${this.apiUrl}${url}`;
    return this.http.get<T>(requestUrl, { params }).pipe(
      tap((response) => {
        if (useCache) {
          const split = url.split('/');
          const lastString = split[1].indexOf('?') > 0 ? split[1].substring(0, split[1].indexOf('?')) : split[1];
          const urlSplit = `${split[0]}_${lastString}`;
          this.sessionService.setItem(urlSplit.toUpperCase(), response);
        }
      }),
      map((res: T) => res),
      catchError(this.handleHttpError)
    );
  }

  getFile(url: string): Observable<any> {
    const requestUrl = `${this.apiUrl}${url}`;
    return this.http.get(requestUrl, { responseType: 'blob' }).pipe(catchError(this.handleHttpError));
  }

  postWithId(url: string, data: any): Observable<Response> {
    return this.doPost(url, data).pipe(map((res: Response) => res));
  }

  //TODO change this <any> for an extended Response that adds the pagination and result field from our api
  post(url: string, data?: any, useCache?: boolean, reqOptions?: { responseType: string }): Observable<any> {
    return this.doPost(url, data, useCache, reqOptions).pipe(map((res: Response) => res));
  }

  putWithId(url: string, data?: any): Observable<Response> {
    return this.doPut(url, data).pipe(map((res: Response) => res));
  }

  patch(url: string, patch: any): Observable<Response> {
    const requestUrl = `${this.apiUrl}${url}`;
    const hasPayload = Array.isArray(patch) ? patch.length > 0 : patch !== null && patch !== undefined;
    if (hasPayload) {
      return this.http.patch(requestUrl, patch).pipe(
        tap(() => this.clearCachedItem(url)),
        map((res: Response) => res),
        catchError(this.handleHttpError)
      );
    } else {
      return of();
    }
  }

  delete(url: string): Observable<boolean> {
    const requestUrl = `${this.apiUrl}${url}`;
    return this.http.delete(requestUrl).pipe(
      tap(() => this.clearCachedItem(url)),
      map(() => true),
      catchError(this.handleHttpError)
    );
  }

  handleHttpError = (error: HttpErrorResponse) => {
    const snackMessageList: SnackMessage[] = [];

    if (error.error?.validations) {
      const errorList = error.error.validations.filter((validation) => validation.severity === errorSeverities.ERROR);
      const warningList = error.error.validations.filter(
        (validation) => validation.severity === errorSeverities.WARNING
      );

      errorList.forEach((validation) => {
        snackMessageList.push(this.buildSnackMessage(validation.message, SnackBarsTypes.ERROR));
      });

      warningList.forEach((validation) => {
        snackMessageList.push(this.buildSnackMessage(validation.message, SnackBarsTypes.WARNING));
      });
    } else if (error.error?.message) {
      snackMessageList.push(this.buildSnackMessage(error.error?.message, SnackBarsTypes.ERROR));
    } else {
      snackMessageList.push(this.buildSnackMessage(error.error?.statusText, SnackBarsTypes.ERROR, error.statusText));
    }

    this.showSnackbars(snackMessageList);

    return throwError(() => error);
  };

  private buildSnackMessage(rawMessage: string, type: SnackBarsTypes, statusText?: string): SnackMessage {
    const regExp = new RegExp('^[A-Za-z]{2}\\d{4}: ?');
    const formattedMessage = `${rawMessage ? rawMessage.replace(regExp, '') : ''}`;
    const message = statusText ? statusText + ' ' + formattedMessage : formattedMessage;
    return {
      type,
      message,
    };
  }

  private showSnackbars(snackMessages: SnackMessage[]) {
    if (snackMessages.length < 1) return;

    const snackRef = this.snackBarService.openSnackBar({
      type: snackMessages[0].type,
      description: snackMessages[0].message,
    });

    snackRef.afterDismissed().subscribe(() => {
      if (snackMessages.length) {
        snackMessages.shift();
        const remainingMessages = [...snackMessages];
        this.showSnackbars(remainingMessages);
      }
    });
  }

  private doPost(
    url: string,
    data: any,
    useCache?: boolean,
    reqOptions?: { responseType: string }
  ): Observable<Response> {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const headers = new HttpHeaders();
    let options = { headers };
    if (reqOptions) {
      options = { ...options, ...reqOptions };
    }
    const requestUrl = `${this.apiUrl}${url}`;

    return this.http.post(requestUrl, data, options).pipe(
      tap((response) => {
        this.clearCachedItem(url);

        // If the "post" is a way to do a "get" for dxData, and it needs to be cached
        if (url.includes('group/search?') && useCache) {
          const splitted = url.split('?');
          const cacheName = splitted[0].replace(/\//gi, '_');
          this.sessionService.setItem(cacheName.substring(1).toUpperCase(), response);
        }
      }),
      map((res: Response) => res),
      catchError(this.handleHttpError)
    );
  }

  private doPut(url: string, data: any): Observable<Response> {
    const requestUrl = `${this.apiUrl}${url}`;
    return this.http.put(requestUrl, data).pipe(
      tap(() => this.clearCachedItem(url)),
      map((res: Response) => res),
      catchError(this.handleHttpError)
    );
  }

  private resetCache(): void {
    // Clear every 2 hours
    this.resetCache$ = interval(7200000).subscribe(() => {
      // Get all "Session storage keys" and filter those that starts with "API_"
      const apiStore = Object.keys(sessionStorage).filter((item) => item.startsWith('API_'));
      apiStore.forEach((item) => this.clearCachedItem(item));
    });
  }

  private clearCachedItem(url: string): void {
    // Check for a '/' if it is an url (from operations that are not "get") to cast as it needs.
    if (url.indexOf('/') > -1) {
      url = `${url.split('/')[1]}_${url.split('/')[2]}`.toUpperCase();
    }

    this.sessionService.removeItem(url);
  }
}
